import { Card, CardBody, Skeleton } from '@vnetwork/ui/primitives';
import { Stat, StatGroup, type StatusTone } from '@vnetwork/ui/data';
import { formatMoney } from '@/lib/format';
import { useInvoiceStatistical } from '@/api/invoices';
import { aggregateInvoiceStats, invoiceStatusTone } from '../invoice-format';

/** Status tone → legend-dot color token. */
const TONE_COLOR: Record<StatusTone, string> = {
  neutral: 'var(--vn-fg-muted)',
  success: 'var(--vn-success-solid)',
  warning: 'var(--vn-warning-solid)',
  danger: 'var(--vn-danger-solid)',
  info: 'var(--vn-info-solid)',
  muted: 'var(--vn-fg-muted)',
};

/**
 * Invoice summary band: one `Stat` card per status (Waiting Confirm · Approved ·
 * Paid · Done · Cancelled · Rejected — in display order, omitting statuses with
 * no data) folded from `/invoices/statistical` via `aggregateInvoiceStats`. Each
 * card shows the summed total amount (VND) as the value and the row count as a
 * hint, with a tone-colored legend dot. Rendered inside a flat `Card`, mirroring
 * the contract stat band. Reflects global totals (no search/filter scoping),
 * like the legacy page.
 */
export function InvoiceStatBand() {
  const { data, isLoading } = useInvoiceStatistical();
  const cards = aggregateInvoiceStats(data?.results ?? []);
  const columns = Math.min(Math.max(cards.length || 1, 1), 6);

  return (
    <Card flat style={{ marginBottom: 'var(--vn-space-4)' }}>
      <CardBody>
        {isLoading ? (
          <StatGroup columns={6}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 56 }} />
            ))}
          </StatGroup>
        ) : (
          <StatGroup columns={columns}>
            {cards.map((c) => (
              <Stat
                key={c.status}
                label={c.label}
                dotColor={TONE_COLOR[invoiceStatusTone(c.status)]}
                value={formatMoney(c.totalAmount)}
                hint={`${c.count.toLocaleString('en-US')} invoice${c.count === 1 ? '' : 's'}`}
              />
            ))}
          </StatGroup>
        )}
      </CardBody>
    </Card>
  );
}
